import { supabase } from './supabase';
import { BillingCycle, PlanCode, PLAN_MAP } from './planCatalog';

export type PaymentProvider = 'mercadopago' | 'paddle';
export type Currency = 'COP' | 'EUR';

export type CreateSubscriptionResult =
  | { provider: 'mercadopago'; redirectUrl: string }
  | { provider: 'paddle'; transactionId: string };

export interface TrialEligibility {
  eligible: boolean;
  reason?: 'already_used' | 'has_paid_plan' | 'not_authenticated';
}

export interface TrialStatus {
  is_in_trial: boolean;
  plan_code?: string;
  trial_ends_at?: string;
  days_remaining?: number;
}

export class SubscriptionService {
  static async createSubscription(
    planCode: PlanCode,
    billingCycle: BillingCycle,
    options?: {
      provider: PaymentProvider;
      currency: Currency;
      upgradingFrom?: PlanCode;
      isTrial?: boolean;
      discountCampaignId?: string;
    },
  ) {
    const plan = PLAN_MAP[planCode];
    if (!plan || plan.isFree) {
      throw new Error('Plan inválido para suscripción');
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      const error = new Error('NOT_AUTHENTICATED');
      (error as { code?: string }).code = 'NOT_AUTHENTICATED';
      throw error;
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const body: Record<string, unknown> = { planCode, billingCycle };
    if (options?.upgradingFrom) body.upgradingFrom = options.upgradingFrom;
    if (options?.isTrial) body.isTrial = true;
    if (options?.discountCampaignId) body.discountCampaignId = options.discountCampaignId;
    if (options?.currency) body.currency = options.currency;

    let response;
    try {
      const provider: PaymentProvider = options?.provider ?? 'mercadopago';
      const fnName = provider === 'paddle' ? 'paddle-transaction' : 'mercadopago-subscription';
      response = await fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey || '',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('El servidor tardó demasiado en responder. Por favor, intenta de nuevo.');
      }
      throw err;
    }

    if (!response.ok) {
      const errorText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(errorText); } catch { /* not JSON */ }

      const mpReason = (parsed?.mpError || parsed?.error) as string | undefined;
      const displayMsg = mpReason
        ? `Error al procesar el pago: ${mpReason}`
        : `Error ${response.status}: ${errorText}`;

      console.error('[SubscriptionService] Edge function error:', {
        status: response.status,
        mpError: parsed?.mpError,
        error: parsed?.error,
        details: parsed?.details,
        raw: errorText,
      });

      throw new Error(displayMsg);
    }

    const data = await response.json();

    if (data?.error) {
      console.error('[SubscriptionService] Unexpected error in ok response:', data);
      throw new Error(data.mpError || data.error || 'Error desconocido');
    }

    const provider: PaymentProvider = options?.provider ?? 'mercadopago';

    if (provider === 'paddle') {
      if (!data?.transactionId) {
        throw new Error('Paddle no devolvió transactionId. Por favor, intenta de nuevo.');
      }
      return { provider: 'paddle', transactionId: data.transactionId as string } as CreateSubscriptionResult;
    }

    if (!data?.init_point) {
      throw new Error('No se pudo iniciar el pago. Por favor, intenta de nuevo.');
    }

    return { provider: 'mercadopago', redirectUrl: data.init_point as string } as CreateSubscriptionResult;
  }

  static async getTrialEligibility(): Promise<TrialEligibility> {
    const { data, error } = await supabase.rpc('get_trial_eligibility');
    if (error) return { eligible: false };
    return data as TrialEligibility;
  }

  static async getTrialStatus(): Promise<TrialStatus> {
    const { data, error } = await supabase.rpc('get_trial_status');
    if (error) return { is_in_trial: false };
    return data as TrialStatus;
  }
}
